import { useRef, useState } from "react";
import { allowedImageMimes } from "../api.js";
import type { DraftImage } from "./ImagesStep.js";

/**
 * AI-routed intake entry: a free-text "what's going on?" box (with optional
 * voice dictation and photos) that the funnel sends to the classifier. The
 * inviting first screen — replaces the bare category grid. On submit the parent
 * classifies the text; if that's slow/unconfident/unconfigured it falls back to
 * the manual grid, so this is purely the happy-path entrypoint.
 */

const EXAMPLES = [
  "Water is leaking under my kitchen sink.",
  "My AC is running but not cooling.",
  "I need a quote for a new patio.",
];

// The Web Speech API isn't in the standard DOM lib types; model just what we use.
interface SpeechRecognitionLike {
  lang: string;
  interimResults: boolean;
  continuous: boolean;
  onresult: ((e: { results: ArrayLike<ArrayLike<{ transcript: string }>> }) => void) | null;
  onend: (() => void) | null;
  onerror: (() => void) | null;
  start: () => void;
  stop: () => void;
}
function getSpeechRecognitionCtor(): (new () => SpeechRecognitionLike) | null {
  const w = window as unknown as {
    SpeechRecognition?: new () => SpeechRecognitionLike;
    webkitSpeechRecognition?: new () => SpeechRecognitionLike;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

export function DescribeIssueStep({
  value,
  onChange,
  images,
  onAddFiles,
  onRemoveImage,
  onStart,
  onChooseManually,
  busy,
}: {
  value: string;
  onChange: (value: string) => void;
  images: DraftImage[];
  onAddFiles: (files: FileList | null) => void;
  onRemoveImage: (localId: string) => void;
  /** Submit the description for classification. */
  onStart: () => void;
  /** Skip the AI and pick a category directly. */
  onChooseManually: () => void;
  /** True while the classifier is running — shows the thinking state. */
  busy: boolean;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [listening, setListening] = useState(false);
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);
  const speechSupported = getSpeechRecognitionCtor() !== null;

  function toggleDictation() {
    if (listening) {
      recognitionRef.current?.stop();
      return;
    }
    const Ctor = getSpeechRecognitionCtor();
    if (!Ctor) return;
    const rec = new Ctor();
    rec.lang = "en-US";
    rec.interimResults = false;
    rec.continuous = false;
    rec.onresult = (e) => {
      let transcript = "";
      for (let i = 0; i < e.results.length; i++) transcript += e.results[i]![0]!.transcript;
      const sep = value.trim().length > 0 ? " " : "";
      onChange((value + sep + transcript).trimStart());
    };
    rec.onend = () => setListening(false);
    rec.onerror = () => setListening(false);
    recognitionRef.current = rec;
    setListening(true);
    rec.start();
  }

  const canStart = value.trim().length > 0 && !busy;

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-bold text-slate-900">Tell us what needs fixed or done.</h1>
        <p className="mt-1 text-sm text-slate-500">
          Describe the issue and we’ll route it to the right local pros.
        </p>
      </div>

      <label className="block">
        <span className="mb-1 block text-sm font-medium text-slate-700">What’s going on?</span>
        <div className="relative">
          <textarea
            autoFocus
            rows={5}
            maxLength={2000}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            disabled={busy}
            className="block w-full rounded-xl border border-slate-300 p-3 pr-12 text-base text-slate-900 shadow-sm focus:border-teal-600 focus:outline-none focus:ring-1 focus:ring-teal-600 disabled:bg-slate-50"
            placeholder={EXAMPLES[0]}
          />
          {speechSupported && (
            <button
              type="button"
              onClick={toggleDictation}
              disabled={busy}
              aria-label={listening ? "Stop dictation" : "Dictate with your voice"}
              aria-pressed={listening}
              className={`absolute right-2 top-2 flex h-9 w-9 items-center justify-center rounded-full border text-lg ${
                listening
                  ? "animate-pulse border-teal-600 bg-teal-600 text-white motion-reduce:animate-none"
                  : "border-slate-300 bg-white text-slate-600"
              }`}
            >
              {/* simple mic glyph */}
              <span aria-hidden>🎤</span>
            </button>
          )}
        </div>
      </label>

      {value.trim().length === 0 && (
        <ul className="space-y-1 text-sm text-slate-400">
          {EXAMPLES.map((ex) => (
            <li key={ex}>“{ex}”</li>
          ))}
        </ul>
      )}

      {/* Optional photos — carried straight into the existing photo step. */}
      <div className="flex flex-wrap items-center gap-2">
        {images.map((img) => (
          <span key={img.localId} className="relative">
            <img
              src={img.previewUrl}
              alt=""
              className="h-14 w-14 rounded-lg border border-slate-200 object-cover"
            />
            <button
              type="button"
              onClick={() => onRemoveImage(img.localId)}
              aria-label="Remove photo"
              className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-slate-800 text-xs text-white"
            >
              ×
            </button>
          </span>
        ))}
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={busy}
          className="flex h-14 items-center gap-2 rounded-lg border border-dashed border-slate-300 px-4 text-sm text-slate-600"
        >
          <span aria-hidden>📷</span> Add photo
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept={allowedImageMimes.join(",")}
          multiple
          className="hidden"
          onChange={(e) => {
            onAddFiles(e.target.files);
            e.target.value = "";
          }}
        />
      </div>

      {busy ? (
        <div
          className="flex min-h-[44px] items-center justify-center gap-2 text-sm font-medium text-teal-700"
          role="status"
          aria-live="polite"
        >
          <span className="flex gap-1" aria-hidden>
            <span className="h-2 w-2 animate-bounce rounded-full bg-teal-600 [animation-delay:-0.3s] motion-reduce:animate-none" />
            <span className="h-2 w-2 animate-bounce rounded-full bg-teal-600 [animation-delay:-0.15s] motion-reduce:animate-none" />
            <span className="h-2 w-2 animate-bounce rounded-full bg-teal-600 motion-reduce:animate-none" />
          </span>
          Reading your description…
        </div>
      ) : (
        <button
          type="button"
          disabled={!canStart}
          onClick={onStart}
          className="min-h-[44px] w-full rounded-xl bg-teal-700 text-base font-semibold text-white shadow-sm active:bg-teal-800 disabled:opacity-60"
        >
          Start my request
        </button>
      )}

      <button
        type="button"
        onClick={onChooseManually}
        disabled={busy}
        className="block w-full text-center text-sm font-medium text-teal-700"
      >
        Prefer to choose a service?
      </button>
    </div>
  );
}
